/**
 * SmartPaste.ts - Intelligent clipboard with format detection and conversion
 * 
 * CHANGE HISTORY:
 * - 251110_0127 [pending] danielbeer: Initial creation
 * 
 * FEATURES:
 * - Automatic format detection using Translatr
 * - Context-aware format suggestions using Contextr
 * - Interactive format selection via Promptr/Selectr
 * - Multi-format conversion and paste
 * 
 * ARCHITECTURE:
 * - All routing through orchestrators (Actr, Translatr, Contextr)
 * - UI via Promptr/Selectr
 * - No direct DOM manipulation
 */

import type { DataFormat } from '@opcentral/translatr';

export interface SmartPasteOptions {
  actr?: any;
  translatr?: any;
  contextr?: any;
  promptr?: any;
  selectr?: any;
}

export interface FormatSuggestion {
  format: DataFormat;
  label: string;
  description: string;
  confidence: number;
  contextRelevance: number;
}

export interface PasteContext {
  element?: HTMLElement;
  contentType?: string; // e.g., 'text/html', 'text/plain'
  surroundingText?: string;
  cursorPosition?: number;
  appContext?: string; // e.g., 'code-editor', 'spreadsheet', 'markdown-editor'
}

/**
 * SmartPaste - Intelligent clipboard with format detection and conversion
 */
export class SmartPaste {
  private actr: any;
  private translatr: any;
  private contextr: any;
  private promptr: any;
  private selectr: any;
  private isMonitoring: boolean = false;
  private lastClipboardContent: string = '';

  constructor(options: SmartPasteOptions = {}) {
    this.actr = options.actr;
    this.translatr = options.translatr;
    this.contextr = options.contextr;
    this.promptr = options.promptr;
    this.selectr = options.selectr;
  }

  /**
   * Start monitoring clipboard events
   */
  startMonitoring() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    
    // Listen for paste events
    document.addEventListener('paste', this.handlePaste.bind(this));
    
    // Listen for copy events to track clipboard content
    document.addEventListener('copy', this.handleCopy.bind(this));
    
    console.log('[SmartPaste] Monitoring started');
  }

  /**
   * Stop monitoring clipboard events
   */
  stopMonitoring() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    document.removeEventListener('paste', this.handlePaste.bind(this));
    document.removeEventListener('copy', this.handleCopy.bind(this));
    
    console.log('[SmartPaste] Monitoring stopped');
  }

  /**
   * Handle copy event - track what was copied
   */
  private async handleCopy(event: ClipboardEvent) {
    try {
      const text = await navigator.clipboard.readText();
      this.lastClipboardContent = text;
      
      // Detect format
      if (this.translatr) {
        const format = await this.translatr.detectFormat(text);
        console.log('[SmartPaste] Copied content detected as:', format);
      }
    } catch (error) {
      console.error('[SmartPaste] Error reading clipboard:', error);
    }
  }

  /**
   * Handle paste event - offer format conversion
   */
  private async handlePaste(event: ClipboardEvent) {
    // Get clipboard text
    const text = event.clipboardData?.getData('text/plain');
    if (!text || text.length === 0) return;

    // Detect format using Translatr
    const detectedFormat = await this.detectFormat(text);
    if (!detectedFormat) return; // No recognized format

    // Get paste context using Contextr
    const context = await this.getPasteContext(event.target as HTMLElement);

    // Get format suggestions based on detected format and context
    const suggestions = await this.getFormatSuggestions(text, detectedFormat, context);
    
    if (suggestions.length === 0) return; // No alternative formats available

    // Prevent default paste
    event.preventDefault();

    // Show format selection UI via Promptr/Selectr
    const selectedFormat = await this.showFormatSelector(suggestions, detectedFormat);
    
    if (!selectedFormat) {
      // User cancelled - paste original
      this.pasteText(text, event.target as HTMLElement);
      return;
    }

    // Convert and paste in selected format
    await this.convertAndPaste(text, detectedFormat, selectedFormat, event.target as HTMLElement);
  }

  /**
   * Detect format of clipboard text using Translatr
   */
  private async detectFormat(text: string): Promise<DataFormat | null> {
    if (!this.translatr) return null;

    try {
      const format = await this.translatr.detectFormat(text);
      
      // Only offer smart paste for recognized structured formats
      const structuredFormats: DataFormat[] = [
        'markdown-table',
        'csv',
        'tsv',
        'json',
        'xml',
        'yaml',
        'toml',
        'html',
        'sql'
      ];
      
      return structuredFormats.includes(format) ? format : null;
    } catch (error) {
      console.error('[SmartPaste] Format detection error:', error);
      return null;
    }
  }

  /**
   * Get paste context using Contextr
   */
  private async getPasteContext(target: HTMLElement): Promise<PasteContext> {
    const context: PasteContext = {
      element: target,
      contentType: 'text/plain'
    };

    if (!this.contextr) return context;

    try {
      // Get context information
      const contextInfo = await this.contextr.getContext({
        element: target,
        includeParents: true,
        includeSiblings: false
      });

      // Determine app context from element attributes and classes
      if (target.classList.contains('monaco-editor') || target.closest('.monaco-editor')) {
        context.appContext = 'code-editor';
      } else if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        context.appContext = 'text-input';
      } else if (target.closest('table') || target.classList.contains('ag-grid')) {
        context.appContext = 'spreadsheet';
      } else if (target.closest('[contenteditable]')) {
        context.appContext = 'rich-text-editor';
      }

      // Get surrounding text for better context
      if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
        context.surroundingText = target.value;
        context.cursorPosition = target.selectionStart || 0;
      }

      return context;
    } catch (error) {
      console.error('[SmartPaste] Context detection error:', error);
      return context;
    }
  }

  /**
   * Get format suggestions based on detected format and context
   */
  private async getFormatSuggestions(
    text: string,
    detectedFormat: DataFormat,
    context: PasteContext
  ): Promise<FormatSuggestion[]> {
    const suggestions: FormatSuggestion[] = [];

    // Define format conversion options based on source format
    const conversionMap: Record<DataFormat, Array<{ format: DataFormat; label: string; description: string }>> = {
      'markdown-table': [
        { format: 'html', label: 'HTML Table', description: 'Paste as HTML table markup' },
        { format: 'csv', label: 'CSV', description: 'Comma-separated values' },
        { format: 'json', label: 'JSON Array', description: 'Array of objects' },
        { format: 'tsv', label: 'TSV', description: 'Tab-separated values' }
      ],
      'csv': [
        { format: 'markdown-table', label: 'Markdown Table', description: 'Formatted markdown table' },
        { format: 'html', label: 'HTML Table', description: 'HTML table markup' },
        { format: 'json', label: 'JSON Array', description: 'Array of objects' },
        { format: 'tsv', label: 'TSV', description: 'Tab-separated values' }
      ],
      'tsv': [
        { format: 'csv', label: 'CSV', description: 'Comma-separated values' },
        { format: 'markdown-table', label: 'Markdown Table', description: 'Formatted markdown table' },
        { format: 'html', label: 'HTML Table', description: 'HTML table markup' },
        { format: 'json', label: 'JSON Array', description: 'Array of objects' }
      ],
      'json': [
        { format: 'csv', label: 'CSV', description: 'Comma-separated values' },
        { format: 'markdown-table', label: 'Markdown Table', description: 'Formatted markdown table' },
        { format: 'yaml', label: 'YAML', description: 'YAML format' },
        { format: 'xml', label: 'XML', description: 'XML format' }
      ],
      'xml': [
        { format: 'json', label: 'JSON', description: 'JSON format' },
        { format: 'yaml', label: 'YAML', description: 'YAML format' },
        { format: 'csv', label: 'CSV', description: 'Flattened to CSV' }
      ],
      'yaml': [
        { format: 'json', label: 'JSON', description: 'JSON format' },
        { format: 'xml', label: 'XML', description: 'XML format' },
        { format: 'toml', label: 'TOML', description: 'TOML format' }
      ],
      'toml': [
        { format: 'json', label: 'JSON', description: 'JSON format' },
        { format: 'yaml', label: 'YAML', description: 'YAML format' }
      ],
      'html': [
        { format: 'markdown', label: 'Markdown', description: 'Convert HTML to Markdown' },
        { format: 'text', label: 'Plain Text', description: 'Strip HTML tags' }
      ],
      'sql': [
        { format: 'json', label: 'JSON', description: 'Parse INSERT statements to JSON' },
        { format: 'csv', label: 'CSV', description: 'Parse INSERT statements to CSV' }
      ]
    };

    const availableConversions = conversionMap[detectedFormat] || [];

    // Calculate relevance based on context
    for (const conversion of availableConversions) {
      let contextRelevance = 0.5; // Base relevance

      // Boost relevance based on app context
      if (context.appContext === 'code-editor') {
        if (['json', 'yaml', 'xml', 'toml'].includes(conversion.format)) {
          contextRelevance = 0.9;
        }
      } else if (context.appContext === 'spreadsheet') {
        if (['csv', 'tsv'].includes(conversion.format)) {
          contextRelevance = 0.9;
        }
      } else if (context.appContext === 'rich-text-editor') {
        if (['html', 'markdown', 'markdown-table'].includes(conversion.format)) {
          contextRelevance = 0.9;
        }
      }

      suggestions.push({
        format: conversion.format,
        label: conversion.label,
        description: conversion.description,
        confidence: 1.0, // We know these conversions are supported
        contextRelevance
      });
    }

    // Sort by context relevance
    suggestions.sort((a, b) => b.contextRelevance - a.contextRelevance);

    return suggestions;
  }

  /**
   * Show format selector UI using Promptr/Selectr
   */
  private async showFormatSelector(
    suggestions: FormatSuggestion[],
    detectedFormat: DataFormat
  ): Promise<DataFormat | null> {
    if (!this.promptr || !this.selectr) {
      // Fallback: use native confirm
      const proceed = confirm(`Detected ${detectedFormat} format. Convert to different format?`);
      return proceed ? suggestions[0]?.format || null : null;
    }

    try {
      // Build items for Selectr
      const items = suggestions.map((suggestion, index) => ({
        id: suggestion.format,
        label: suggestion.label,
        description: suggestion.description,
        icon: this.getFormatIcon(suggestion.format),
        metadata: {
          confidence: suggestion.confidence,
          contextRelevance: suggestion.contextRelevance,
          relevanceIndicator: suggestion.contextRelevance > 0.7 ? '⭐' : ''
        }
      }));

      // Add "Other..." option
      items.push({
        id: 'other',
        label: 'Other...',
        description: 'Choose from all available formats',
        icon: '🔄',
        metadata: {}
      });

      // Add "Keep Original" option
      items.push({
        id: 'original',
        label: 'Keep Original',
        description: `Paste as ${detectedFormat}`,
        icon: '📋',
        metadata: {}
      });

      // Show selector via Actr action
      const result = await this.actr.execute('selectr:show', {
        items,
        title: `Smart Paste: ${detectedFormat} detected`,
        searchable: true,
        grouped: false,
        multiSelect: false
      });

      if (!result || result.cancelled) {
        return null; // User cancelled
      }

      const selectedId = result.selected?.[0]?.id;

      if (selectedId === 'original') {
        return null; // Keep original format
      }

      if (selectedId === 'other') {
        // Show all available formats
        return await this.showAllFormatsSelector(detectedFormat);
      }

      return selectedId as DataFormat;
    } catch (error) {
      console.error('[SmartPaste] Error showing format selector:', error);
      return null;
    }
  }

  /**
   * Show all available formats selector
   */
  private async showAllFormatsSelector(sourceFormat: DataFormat): Promise<DataFormat | null> {
    const allFormats: DataFormat[] = [
      'json', 'xml', 'yaml', 'toml', 'csv', 'tsv',
      'markdown', 'markdown-table', 'html', 'text',
      'sql', 'ini', 'env'
    ];

    const items = allFormats
      .filter(f => f !== sourceFormat)
      .map(format => ({
        id: format,
        label: this.getFormatLabel(format),
        description: `Convert to ${format}`,
        icon: this.getFormatIcon(format),
        metadata: {}
      }));

    const result = await this.actr.execute('selectr:show', {
      items,
      title: 'Choose target format',
      searchable: true,
      grouped: false,
      multiSelect: false
    });

    if (!result || result.cancelled) {
      return null;
    }

    return result.selected?.[0]?.id as DataFormat;
  }

  /**
   * Convert and paste text in selected format
   */
  private async convertAndPaste(
    text: string,
    sourceFormat: DataFormat,
    targetFormat: DataFormat,
    target: HTMLElement
  ) {
    if (!this.translatr) {
      console.error('[SmartPaste] Translatr not available for conversion');
      this.pasteText(text, target);
      return;
    }

    try {
      // Convert using Translatr
      const converted = await this.translatr.convert(text, sourceFormat, targetFormat);
      
      // Paste converted text
      this.pasteText(converted, target);
      
      // Show success notification via Actr
      if (this.actr) {
        await this.actr.execute('notifyr:show', {
          message: `Converted from ${sourceFormat} to ${targetFormat}`,
          type: 'success',
          duration: 2000
        });
      }
    } catch (error) {
      console.error('[SmartPaste] Conversion error:', error);
      
      // Show error notification
      if (this.actr) {
        await this.actr.execute('notifyr:show', {
          message: `Failed to convert from ${sourceFormat} to ${targetFormat}`,
          type: 'error',
          duration: 3000
        });
      }
      
      // Fallback to original text
      this.pasteText(text, target);
    }
  }

  /**
   * Paste text into target element
   */
  private pasteText(text: string, target: HTMLElement) {
    if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
      const start = target.selectionStart || 0;
      const end = target.selectionEnd || 0;
      const before = target.value.substring(0, start);
      const after = target.value.substring(end);
      target.value = before + text + after;
      target.selectionStart = target.selectionEnd = start + text.length;
      
      // Trigger input event
      target.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (target.isContentEditable || target.closest('[contenteditable]')) {
      // For contenteditable elements
      document.execCommand('insertText', false, text);
    } else {
      // Fallback: try to insert at cursor
      try {
        document.execCommand('insertText', false, text);
      } catch (error) {
        console.error('[SmartPaste] Could not paste text:', error);
      }
    }
  }

  /**
   * Get icon for format
   */
  private getFormatIcon(format: DataFormat): string {
    const iconMap: Record<string, string> = {
      'json': '{ }',
      'xml': '</>',
      'yaml': '📝',
      'toml': '⚙️',
      'csv': '📊',
      'tsv': '📋',
      'markdown': '📄',
      'markdown-table': '📊',
      'html': '🌐',
      'text': '📝',
      'sql': '🗄️',
      'ini': '⚙️',
      'env': '🔐'
    };
    return iconMap[format] || '📄';
  }

  /**
   * Get label for format
   */
  private getFormatLabel(format: DataFormat): string {
    const labelMap: Record<string, string> = {
      'json': 'JSON',
      'xml': 'XML',
      'yaml': 'YAML',
      'toml': 'TOML',
      'csv': 'CSV',
      'tsv': 'TSV',
      'markdown': 'Markdown',
      'markdown-table': 'Markdown Table',
      'html': 'HTML',
      'text': 'Plain Text',
      'sql': 'SQL',
      'ini': 'INI',
      'env': 'ENV'
    };
    return labelMap[format] || format.toUpperCase();
  }
}

/**
 * Create SmartPaste instance
 */
export function createSmartPaste(options: SmartPasteOptions = {}): SmartPaste {
  return new SmartPaste(options);
}
